import { ClipboardList, Building2, MapPin } from "lucide-react";

import { formatMinutes } from "../../shared/duration";

import type { CollectionColumn, CollectionRecord } from "../components/CollectionView";
import { MATERIAL_UNIT_COMPACT_LABELS } from "../../shared/material-units";
import { StatusBadge } from "../components/StatusBadge";
import type { RouterOutputs } from "./trpc";

type Intervention = RouterOutputs["interventions"]["list"][number];
type Site = RouterOutputs["sites"]["list"][number];

const statusLabels = {
  scheduled: "Programmato",
  in_progress: "In corso",
  completed: "Completato",
  cancelled: "Annullato",
} as const;

const priorityLabels = {
  1: "Alta",
  2: "Normale",
  3: "Bassa",
} as const;

const siteKindLabels = {
  residence: "Residence",
  condominium: "Condominio",
  hotel: "Hotel",
  villa: "Villa",
  private_home: "Privato",
  other: "Altro",
} as const;

const unitLabels = MATERIAL_UNIT_COMPACT_LABELS;

const materialStatusLabels = {
  requested: "Richiesto",
  ordered: "Ordinato",
  partially_received: "Arrivato in parte",
  received: "Ricevuto",
  used: "Utilizzato",
  cancelled: "Annullato",
} as const;

/**
 * Negli elenchi comanda la struttura: il cliente ragiona per luogo da visitare,
 * non per titolo dell'intervento, che resta come informazione di secondo piano.
 */
export const interventionTitleLabel = "Struttura";

export const interventionColumns: CollectionColumn[] = [
  { key: "intervento", label: "Intervento" },
  { key: "dataOra", label: "Data e ora" },
  { key: "indirizzo", label: "Indirizzo" },
  { key: "tipoLavoro", label: "Tipo di lavoro" },
  { key: "attivita", label: "Attività" },
  { key: "collaboratori", label: "Collaboratori" },
  { key: "tempo", label: "Tempo" },
  { key: "materiali", label: "Materiali" },
  { key: "foto", label: "Foto" },
  { key: "stato", label: "Stato" },
  { key: "priorita", label: "Priorità" },
  { key: "note", label: "Note" },
];

export const siteColumns: CollectionColumn[] = [
  { key: "tipo", label: "Tipo" },
  { key: "indirizzo", label: "Indirizzo" },
  { key: "referente", label: "Referente" },
  { key: "telefono", label: "Telefono" },
  { key: "coordinate", label: "Coordinate" },
  { key: "note", label: "Note" },
];

function formatDecimal(value: number | string, maximumFractionDigits: number) {
  return new Intl.NumberFormat("it-IT", { maximumFractionDigits, useGrouping: false }).format(Number(value));
}

function formatMaterial(
  quantity: number,
  unit: keyof typeof unitLabels,
  label: string,
  status: keyof typeof materialStatusLabels,
) {
  return `${label}, ${formatDecimal(quantity, 4)} ${unitLabels[unit]} — ${materialStatusLabels[status]}`;
}

function formatDateTime(value: Date | string) {
  return new Date(value).toLocaleString("it-IT", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Le attività si leggono per categoria: in evidenza il lavoro generico (Piscina, Giardinaggio)
 * e dentro, a scomparsa, le attività specifiche che lo compongono.
 * La gerarchia non è nuova, viene dal catalogo: qui cambia soltanto come si mostra.
 */
export function groupActivitiesByCategory<T extends { label: string; categoryName?: string | null }>(activities: T[]) {
  const groups = new Map<string, T[]>();
  for (const activity of activities) {
    const category = activity.categoryName?.trim() || "Senza categoria";
    groups.set(category, [...(groups.get(category) ?? []), activity]);
  }
  return [...groups].map(([category, items]) => ({ category, items }));
}

export function toInterventionRecord(intervention: Intervention, index: number): CollectionRecord {
  const activityGroups = groupActivitiesByCategory(intervention.activities);
  const categories = activityGroups.map((group) => group.category).join(" · ");
  const activities = intervention.activities
    .map((activity) => {
      const label = activity.categoryName ? `${activity.categoryName}: ${activity.label}` : activity.label;
      return `${label} — ${activity.completed ? "completata" : "da completare"}`;
    })
    .join(", ");
  const activityDetail = activityGroups.length === 0 ? null : (
    <details className="activity-breakdown">
      <summary aria-label={`Mostra le attività di ${intervention.siteName}`}>
        <span className="activity-breakdown__categories" aria-hidden="true">{categories}</span>
        <span className="activity-breakdown__command" aria-hidden="true">Vedi attività</span>
      </summary>
      <ul>
        {activityGroups.map((group) => (
          <li key={group.category}>
            <strong>{group.category}</strong>
            <span>{group.items.map((item) => `${item.label}${item.completed ? " (fatta)" : ""}`).join(", ")}</span>
          </li>
        ))}
      </ul>
    </details>
  );
  const collaborators = intervention.assignees.map((assignee) => assignee.name).join(", ");
  const time = intervention.recordedMinutes > 0 ? formatMinutes(intervention.recordedMinutes) : "";
  const timer = intervention.hasActiveTimer ? "Timer attivo" : "";
  const materials = intervention.materials
    .map((material) => formatMaterial(material.quantity, material.unit, material.label, material.status))
    .join("; ");
  const photos = `Prima ${intervention.photoCounts.before}, durante ${intervention.photoCounts.during}, dopo ${intervention.photoCounts.after}`;

  return {
    id: intervention.id,
    number: String(index + 1).padStart(2, "0"),
    typeLabel: statusLabels[intervention.status],
    icon: <Building2 aria-hidden="true" />,
    // Titolo della card e prima colonna della Lista: la struttura, cioè dove si va a lavorare.
    title: intervention.siteName,
    subtitle: <><ClipboardList aria-hidden="true" />{intervention.title}</>,
    statusBadge: <StatusBadge status={intervention.status} />,
    hiddenInCards: ["stato"],
    hiddenInCubes: ["intervento"],
    detailHref: `/interventi/${intervention.id}`,
    cells: {
      intervento: { text: intervention.title },
      dataOra: { text: [formatDateTime(intervention.scheduledStart), intervention.scheduledEnd ? formatDateTime(intervention.scheduledEnd) : null].filter(Boolean).join(" – ") },
      indirizzo: intervention.siteAddress ? { text: intervention.siteAddress, content: <><MapPin aria-hidden="true" size={14} /> {intervention.siteAddress}</> } : null,
      tipoLavoro: categories ? { text: categories } : null,
      attivita: activities ? { text: activities, content: activityDetail ?? undefined } : null,
      collaboratori: collaborators ? { text: collaborators } : null,
      tempo: time || timer ? { text: [time, timer].filter(Boolean).join(" · ") } : null,
      materiali: materials ? { text: materials } : null,
      foto: { text: photos },
      stato: { text: statusLabels[intervention.status], content: <StatusBadge status={intervention.status} /> },
      priorita: { text: priorityLabels[intervention.priority as keyof typeof priorityLabels] ?? `Priorità ${intervention.priority}` },
      note: intervention.description ? { text: intervention.description, expandable: true } : null,
    },
  };
}

export function toSiteRecord(site: Site, index: number): CollectionRecord {
  const coordinates = [site.latitude, site.longitude]
    .filter((value) => value !== null && value !== undefined)
    .map((value) => formatDecimal(value!, 6))
    .join(" · ");
  return {
    id: site.id,
    number: String(index + 1).padStart(2, "0"),
    typeLabel: siteKindLabels[site.kind],
    icon: <Building2 aria-hidden="true" />,
    title: site.name,
    subtitle: site.address ? <><MapPin aria-hidden="true" />{site.address}</> : undefined,
    hiddenInCubes: ["tipo", "indirizzo"],
    cells: {
      tipo: { text: siteKindLabels[site.kind] },
      indirizzo: site.address ? { text: site.address } : null,
      referente: site.contactName ? { text: site.contactName } : null,
      telefono: site.contactPhone ? { text: site.contactPhone } : null,
      coordinate: coordinates ? { text: coordinates } : null,
      note: site.notes ? { text: site.notes, expandable: true } : null,
    },
    // La scheda della struttura e dove vive lo storico delle misurazioni fatte in quel posto.
    detailHref: `/strutture/${site.id}`,
  };
}
